import { buildConfig } from './config';
import { exportReport, setLastReport } from './exporter';
import type { GroupResult, LabConfig, LabReport, TestResult } from './types';
import {
  createVisibilityTracker,
  getDeviceUuid,
  getSavedCpuModel,
  getSavedDeviceLabel,
  getShownMemes,
  markMemeShown,
  now,
  resetShownMemes,
  setSavedCpuModel,
  setSavedDeviceLabel,
} from './utils';

import { runEnvironmentTests } from './tests/A_environment';
import { runCpuTests } from './tests/B_cpu';
import { runMemoryTests } from './tests/C_memory';
import { runGpuTests } from './tests/D_gpu';
import { runWorkerTests } from './tests/E_workers';
import { runTimerTests } from './tests/F_timers';
import { runNetworkTests } from './tests/G_network';
import { runOtherTests } from './tests/H_other';
import { runWasmBoundaryTests } from './tests/I_wasm_boundary';
import { runCacheTlbTests } from './tests/J_cache_tlb';
import { runCryptoTests } from './tests/K_crypto';
import { runStorageTests } from './tests/L_storage';
import { listMemes } from './memes';

export { buildConfig } from './config';
export type { LabConfig, LabReport, TestResult } from './types';

const VERSION = '0.1.0';

async function runGroup(
  name: string,
  runner: () => Promise<TestResult[]> | TestResult[]
): Promise<GroupResult> {
  const t0 = now();
  const tests = await runner();
  // Ensure each test's group matches the wrapper name (handles prefixed runs).
  for (const t of tests) t.group = name;
  return { group: name, durationMs: now() - t0, tests };
}

export async function runLab(
  mode: 'full' | 'prod' = 'full',
  overrides?: Partial<LabConfig> & { label?: string; cpuModel?: string }
): Promise<LabReport> {
  const tracker = createVisibilityTracker();
  const config = buildConfig(mode, { ...overrides, visibilityTracker: tracker });
  const startedAt = new Date().toISOString();
  const t0 = now();

  try {
    const groups: GroupResult[] = [];

    // --- Phase 1: prod snapshot (only in full mode) ---
    // Run the prod-config groups first so you get a quick prod baseline
    // before the comprehensive full tests begin.
    if (mode === 'full') {
      const prodConfig = buildConfig('prod', { ...overrides, visibilityTracker: tracker });
      groups.push(await runGroup('prod_A_environment', () => runEnvironmentTests(prodConfig)));
      groups.push(await runGroup('prod_B_cpu', () => runCpuTests(prodConfig)));
      groups.push(await runGroup('prod_J_cache_tlb', () => runCacheTlbTests(prodConfig)));
      groups.push(await runGroup('prod_C_memory', () => runMemoryTests(prodConfig)));
      groups.push(await runGroup('prod_D_gpu', () => runGpuTests(prodConfig)));
      groups.push(await runGroup('prod_H_other', () => runOtherTests(prodConfig)));
    }

    // --- Phase 2: comprehensive run ---
    groups.push(await runGroup('A_environment', () => runEnvironmentTests(config)));
    groups.push(await runGroup('B_cpu', () => runCpuTests(config)));

    if (config.mode !== 'prod') {
      groups.push(await runGroup('I_wasm_boundary', () => runWasmBoundaryTests(config)));
    }

    groups.push(await runGroup('J_cache_tlb', () => runCacheTlbTests(config)));

    if (config.mode !== 'prod') {
      groups.push(await runGroup('K_crypto', () => runCryptoTests(config)));
      groups.push(await runGroup('L_storage', () => runStorageTests()));
    }

    groups.push(await runGroup('C_memory', () => runMemoryTests(config)));

    groups.push(await runGroup('D_gpu', () => runGpuTests(config)));

    if (config.enableWorkers) {
      groups.push(await runGroup('E_workers', () => runWorkerTests(config)));
    }

    if (config.mode !== 'prod') {
      groups.push(await runGroup('F_timers', () => runTimerTests(config)));
    }

    if (config.enableNetwork) {
      groups.push(await runGroup('G_network', () => runNetworkTests(config)));
    }

    groups.push(await runGroup('H_other', () => runOtherTests(config)));

    const totalDurationMs = now() - t0;
    const totalTests = groups.reduce((sum, g) => sum + g.tests.length, 0);
    const passedTests = groups.reduce(
      (sum, g) => sum + g.tests.filter((t) => t.success).length,
      0
    );

    const report: LabReport = {
      version: VERSION,
      mode,
      label: overrides?.label,
      cpuModel: overrides?.cpuModel,
      startedAt,
      finishedAt: new Date().toISOString(),
      totalDurationMs,
      environment: {
        url: window.location.href,
        timestamp: Date.now(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        visibility: tracker.getState(),
        deviceUuid: getDeviceUuid(),
        deviceModel: overrides?.label,
        cpuModel: overrides?.cpuModel,
      },
      groups,
      summary: {
        totalTests,
        passedTests,
        failedTests: totalTests - passedTests,
      },
    };

    setLastReport(report);
    await exportReport(report, config);
    return report;
  } finally {
    tracker.destroy();
  }
}

function downloadReport(report: LabReport, machineLabel: string): void {
  const label = machineLabel.replace(/[^a-z0-9_-]/gi, '_').slice(0, 32) || 'unknown';
  const ts = new Date().toISOString().replace(/[:.]/g, '-');
  const filename = `report-${label}-${ts}.json`;
  const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function bindUi(): void {
  const btnStart = document.getElementById('btn-start') as HTMLButtonElement | null;
  const btnSave = document.getElementById('btn-save') as HTMLButtonElement | null;
  const machineLabel = document.getElementById('machine-label') as HTMLTextAreaElement | null;
  const runEstimate = document.getElementById('run-estimate') as HTMLDivElement | null;
  const runNotice = document.getElementById('run-notice') as HTMLDivElement | null;
  const cpuModel = document.getElementById('cpu-model') as HTMLTextAreaElement | null;
  const cpuChips = document.getElementById('cpu-chips') as HTMLDivElement | null;
  const repeatCount = document.getElementById('repeat-count') as HTMLInputElement | null;
  const status = document.getElementById('status') as HTMLDivElement | null;
  const resultsEl = document.getElementById('results') as HTMLDivElement | null;
  const resultsBody = document.getElementById('results-body') as HTMLDivElement | null;
  const timerEl = document.getElementById('timer') as HTMLDivElement | null;
  const memeContainer = document.getElementById('meme-container') as HTMLDivElement | null;
  const memeImg = document.getElementById('meme-img') as HTMLImageElement | null;
  const labelChips = document.getElementById('label-chips') as HTMLDivElement | null;

  if (!btnStart || !status || !resultsEl || !resultsBody) return;

  const ui = { btnStart, btnSave, machineLabel, runEstimate, runNotice, cpuModel, cpuChips, repeatCount, status, resultsEl, resultsBody, timerEl, memeContainer, memeImg, labelChips };
  let lastReport: LabReport | null = null;
  const memes: string[] = listMemes();
  let timerId: number | null = null;
  let isRunActive = false;

  const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
    if (!isRunActive) return;
    e.preventDefault();
    e.returnValue = '';
  };

  function setStatus(text: string): void {
    ui.status.textContent = text;
  }

  function formatDurationRange(minSeconds: number, maxSeconds: number): string {
    const format = (seconds: number) => {
      if (seconds < 90) return `~${Math.round(seconds)} сек`;
      const minutes = seconds / 60;
      return minutes < 10 ? `~${minutes.toFixed(1)} мин` : `~${Math.round(minutes)} мин`;
    };
    return `${format(minSeconds)} – ${format(maxSeconds)}`;
  }

  function updateRunEstimate(): void {
    if (!ui.runEstimate) return;
    const repeats = Math.max(1, parseInt(ui.repeatCount?.value || '10', 10) || 10);
    const pauseSeconds = Math.max(0, repeats - 1) * 5;
    const minSeconds = repeats * 35 + pauseSeconds;
    const maxSeconds = repeats * 70 + pauseSeconds;
    ui.runEstimate.textContent = `Примерно ${formatDurationRange(minSeconds, maxSeconds)}. Обычно один запуск укладывается примерно в 30 секунд.`;
  }

  function setRunNoticeVisible(visible: boolean): void {
    if (!ui.runNotice) return;
    ui.runNotice.style.display = visible ? 'block' : 'none';
  }

  function startTimer(): void {
    if (timerId !== null) return;
    const start = now();
    ui.timerEl!.style.display = 'block';

    function tick() {
      ui.timerEl!.textContent = `Идёт тестирование: ${((now() - start) / 1000).toFixed(1)} с`;
      if (timerId !== null) timerId = requestAnimationFrame(tick);
    }
    tick();
  }

  function stopTimer(): void {
    if (timerId !== null) {
      cancelAnimationFrame(timerId);
      timerId = null;
    }
    ui.timerEl!.style.display = 'none';
  }

  function renderReport(report: LabReport): void {
    lastReport = report;
    if (ui.btnSave) ui.btnSave.disabled = false;

    ui.resultsEl.style.display = 'block';
    ui.resultsBody.innerHTML = '';

    const summary = document.createElement('div');
    summary.innerHTML = `
      <strong>Устройство:</strong> ${report.label || 'не указано'} ·
      <strong>Процессор:</strong> ${report.cpuModel || 'не указан'}<br>
      <strong>Режим:</strong> ${report.mode} ·
      <strong>Общее время:</strong> ${report.totalDurationMs.toFixed(1)} мс ·
      <strong>Тесты:</strong> ${report.summary.passedTests}/${report.summary.totalTests}
    `;
    summary.style.marginBottom = '1rem';
    ui.resultsBody.appendChild(summary);

    for (const group of report.groups) {
      const groupEl = document.createElement('div');
      groupEl.className = 'group';
      groupEl.innerHTML = `<div class="group-title">${group.group} (${group.durationMs.toFixed(1)} ms)</div>`;

      for (const test of group.tests) {
        const meta: string[] = [];
        if (test.stats) meta.push(`median=${test.stats.median.toFixed(3)}ms cv=${(test.stats.cv * 100).toFixed(1)}%`);
        if (test.error) meta.push(`ошибка=${test.error}`);

        const testEl = document.createElement('div');
        testEl.className = `test ${test.success ? 'ok' : 'err'}`;
        testEl.innerHTML = `
          <div class="test-name">${test.name}</div>
          <div class="test-meta">${meta.join(' · ')}</div>
        `;
        groupEl.appendChild(testEl);
      }
      ui.resultsBody.appendChild(groupEl);
    }
  }

  function pickNextMeme(): string | null {
    if (memes.length === 0) return null;
    let shown = getShownMemes();
    let unseen = memes.filter((m) => !shown.includes(m));
    if (unseen.length === 0) {
      resetShownMemes();
      unseen = memes;
    }
    if (unseen.length === 0) return null;
    const pick = unseen[Math.floor(Math.random() * unseen.length)];
    markMemeShown(pick);
    return pick;
  }

  function showMeme() {
    if (!ui.memeContainer || !ui.memeImg) return;
    const label = ui.machineLabel?.value.trim() || '';
    if (label.length === 0 || memes.length === 0) {
      ui.memeContainer.style.display = 'none';
      return;
    }
    const meme = pickNextMeme();
    if (!meme) {
      ui.memeContainer.style.display = 'none';
      return;
    }
    ui.memeImg.src = meme;
    ui.memeImg.onload = () => { ui.memeContainer!.style.display = 'block'; };
    ui.memeImg.onerror = () => { ui.memeContainer!.style.display = 'none'; };
  }

  if (ui.labelChips && ui.machineLabel) {
    ui.labelChips.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const value = target.getAttribute('data-value');
      if (!value || !ui.machineLabel) return;
      const current = ui.machineLabel.value.trim();
      ui.machineLabel.value = current + (current ? ' ' : '') + value;
    });
  }

  if (ui.cpuChips && ui.cpuModel) {
    ui.cpuChips.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const value = target.getAttribute('data-value');
      if (!value || !ui.cpuModel) return;
      const current = ui.cpuModel.value.trim();
      ui.cpuModel.value = current + (current ? ' ' : '') + value;
    });
  }

  const urlParams = new URL(window.location.href).searchParams;

  const urlLabel = urlParams.get('label');
  const urlCpuModel = urlParams.get('cpuModel');
  const savedLabel = getSavedDeviceLabel();
  const savedCpuModel = getSavedCpuModel();
  if (urlLabel && ui.machineLabel) {
    ui.machineLabel.value = urlLabel;
  } else if (savedLabel && ui.machineLabel) {
    ui.machineLabel.value = savedLabel;
  }
  if (urlCpuModel && ui.cpuModel) {
    ui.cpuModel.value = urlCpuModel;
  } else if (savedCpuModel && ui.cpuModel) {
    ui.cpuModel.value = savedCpuModel;
  }

  const urlRepeat = urlParams.get('repeat');
  const repeatValue = Math.max(1, parseInt(urlRepeat || '10', 10) || 10);
  if (ui.repeatCount) {
    ui.repeatCount.value = String(repeatValue);
    ui.repeatCount.addEventListener('input', updateRunEstimate);
    ui.repeatCount.addEventListener('change', updateRunEstimate);
  }
  updateRunEstimate();
  setRunNoticeVisible(false);

  if (ui.machineLabel) {
    ui.machineLabel.dispatchEvent(new Event('input'));
  }
  if (ui.cpuModel) {
    ui.cpuModel.dispatchEvent(new Event('input'));
  }
  if (ui.repeatCount) {
    ui.repeatCount.dispatchEvent(new Event('input'));
  }

  function redirectToNextBatch(): boolean {
    const batchRaw = urlParams.get('batch') || '';
    const batch = batchRaw.split(',').filter(Boolean);
    if (batch.length === 0) return false;
    const next = batch.shift()!;
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('label', next);
    nextUrl.searchParams.set('batch', batch.join(','));
    nextUrl.searchParams.set('autostart', '1');
    if (ui.cpuModel?.value.trim()) nextUrl.searchParams.set('cpuModel', ui.cpuModel.value.trim());
    if (ui.repeatCount?.value) nextUrl.searchParams.set('repeat', ui.repeatCount.value);
    if (!nextUrl.searchParams.has('mode')) nextUrl.searchParams.set('mode', 'full');
    setStatus(`Переход к следующему запуску: ${next}`);
    setTimeout(() => { window.location.href = nextUrl.toString(); }, 1000);
    return true;
  }

  async function handleRun() {
    ui.btnStart.disabled = true;
    ui.btnStart.classList.add('loading');
    ui.btnStart.textContent = 'Идёт тестирование...';
    if (ui.btnSave) ui.btnSave.disabled = true;
    isRunActive = true;
    setRunNoticeVisible(true);
    window.addEventListener('beforeunload', beforeUnloadHandler);
    startTimer();
    setStatus('Подготовка к запуску...');

    // Some headless engines skip rAF; race against a timeout so we never hang.
    await new Promise<void>((resolve) => {
      const timeout = setTimeout(() => { cancelAnimationFrame(raf); resolve(); }, 100);
      const raf = requestAnimationFrame(() => { clearTimeout(timeout); resolve(); });
    });

    setStatus('Запускаем тестирование...');

    try {
      const deviceModel = ui.machineLabel?.value.trim() || '';
      const cpuModelValue = ui.cpuModel?.value.trim() || '';
      if (!deviceModel) {
        setStatus('Укажи модель устройства перед запуском.');
        return;
      }

      const cacheModeParam = urlParams.get('cacheMode');
      const cacheMode = cacheModeParam === 'fast' || cacheModeParam === 'research' ? cacheModeParam : undefined;
      const modeParam = urlParams.get('mode');
      const mode: 'full' | 'prod' = modeParam === 'prod' ? 'prod' : 'full';
      const enableWebGL = urlParams.get('webgl') !== '0';

      const totalRepeats = Math.max(1, parseInt(ui.repeatCount?.value || urlParams.get('repeat') || '10', 10) || 10);

      for (let run = 1; run <= totalRepeats; run++) {
        setStatus(run === 1 ? 'Идёт первый запуск...' : `Идёт запуск ${run} из ${totalRepeats}...`);

        const report = await runLab(mode, { debug: false, cacheMode, enableWebGL, enableWebGL2: enableWebGL, label: deviceModel, cpuModel: cpuModelValue || undefined });
        setStatus(`Запуск ${run} из ${totalRepeats} завершён за ${report.totalDurationMs.toFixed(1)} мс. Успешно: ${report.summary.passedTests}/${report.summary.totalTests}.`);
        renderReport(report);

        if (run < totalRepeats) {
          setStatus(`Небольшая пауза 5 секунд перед запуском ${run + 1} из ${totalRepeats}...`);
          await new Promise<void>((resolve) => setTimeout(resolve, 5000));
        }
      }

      showMeme();

      if (deviceModel) setSavedDeviceLabel(deviceModel);
      setSavedCpuModel(cpuModelValue);

      if (redirectToNextBatch()) return;
    } catch (err) {
      setStatus(`Ошибка: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      isRunActive = false;
      setRunNoticeVisible(false);
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      stopTimer();
      ui.btnStart.classList.remove('loading');
      ui.btnStart.textContent = 'Запустить тестирование';
      ui.btnStart.disabled = false;
    }
  }

  ui.btnStart.addEventListener('click', handleRun);

  if (ui.btnSave) {
    ui.btnSave.addEventListener('click', () => {
      if (lastReport) downloadReport(lastReport, ui.machineLabel?.value || 'unknown');
    });
  }

  if (urlParams.get('autostart') === '1') {
    setTimeout(handleRun, 500);
  }
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindUi);
  } else {
    bindUi();
  }
}

if (typeof window !== 'undefined') {
  (window as any).runLab = runLab;
}
